/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
import React, { useCallback, useRef, Fragment } from "react";
import PropTypes from "prop-types";
import Panel from "../GUI/Panel";
import TopologyWidget from "./Topology";
import EventHandlersManager from "../../controllers/EventHandlersManager";
import "../../aframe_components/GrabMove";

function TopologyAdvanced({
    width,
    stateName,
    height,
    depth,
    setZoom,
    setPosition,
    setLockControls,
    data,
    deviceData,
    nodeGenerator,
    loading,
    setLoading,
    linkDirectionalParticleWidth,
    ...otherAttributes
}) {
    const dragPanel = useRef();
    const focused = useRef(false);
    const root = useRef();

    const linkWidth = useCallback((link) => {
        if (link.color === "#ff0") return 3;
    }, []);

    const zoomHandler = useCallback(
        (e) => {
            e.stopPropagation();
            const deadZone = 0.3;

            if (!focused.current) return;

            const isController = e.type !== "wheel";

            let y = isController ? e.detail.y * 2 : e.deltaY;
            if (Math.abs(y) > 15) y = 15 * (y > 0 ? 1 : -1);
            if (Math.abs(y) > deadZone) {
                setZoom((old) => {
                    const z = old - y / 50;
                    if (z < -4 || z > 8) return old;
                    return z;
                });
            }
        },
        [setZoom]
    );
    if (!data) return null;
    data.data.nodeGenerator = nodeGenerator;

    return (
        <a-entity class="TopologyAdvanced" {...otherAttributes}>
            <EventHandlersManager
                events={{
                    wheel: (e) => {
                        zoomHandler(e);
                    },
                    thumbstickmoved: (e) => {
                        zoomHandler(e);
                    },
                    trackpadmoved: (e) => {
                        zoomHandler(e);
                    },
                    touchpadmoved: (e) => {
                        zoomHandler(e);
                    },
                    mouseenter: {
                        element: dragPanel,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = true;
                        }
                    },
                    mouseleave: {
                        element: dragPanel,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = false;
                        }
                    }
                }}
                enabled={deviceData.canModify}
            />
            <EventHandlersManager
                events={{
                    mouseenter: {
                        element: root,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = true;
                        }
                    },
                    mouseleave: {
                        element: root,
                        isReactRef: true,
                        callback: (e) => {
                            focused.current = false;
                        }
                    }
                }}
                enabled={deviceData.canModify}
            />
            {(!deviceData.inVRMode ||
                deviceData.areControllersConnected ||
                deviceData.areControllersConnected ||
                (deviceData.inVRMode &&
                    !deviceData.areControllersConnected &&
                    !deviceData.isMobile)) && (
                    <>
                        <EventHandlersManager
                            events={{
                                mousedown: {
                                    element: dragPanel,
                                    isReactRef: true,
                                    callback: (e) => {
                                        setLockControls(true)
                                        root.current.setAttribute("grab-move", {
                                            lockCamera:
                                                deviceData.inVRMode &&
                                                !deviceData.areControllersConnected &&
                                                !deviceData.isMobile,
                                            setPosition: setPosition,
                                            setLockControls: setLockControls,
                                        });
                                    }
                                },
                                mouseup: {
                                    element: dragPanel,
                                    isReactRef: true,
                                    callback: (e) => {
                                        setLockControls(false)
                                        root.current.removeAttribute("grab-move");
                                    }
                                }
                            }}
                            enabled={deviceData.canModify}
                        />
                        <EventHandlersManager
                            events={{
                                mousedown: {
                                    element: root,
                                    isReactRef: true,
                                    callback: (e) => {
                                        setLockControls(true)
                                        root.current.setAttribute("grab-move", {
                                            lockCamera:
                                                deviceData.inVRMode &&
                                                !deviceData.areControllersConnected &&
                                                !deviceData.isMobile,
                                            setPosition: setPosition,
                                            setLockControls: setLockControls,
                                        });
                                    }
                                },
                                mouseup: {
                                    element: root,
                                    isReactRef: true,
                                    callback: (e) => {
                                        setLockControls(false)
                                        root.current.removeAttribute("grab-move");
                                    }
                                }
                            }}
                            enabled={deviceData.canModify}
                        />
                    </>
                )}
            <a-entity>
                <a-plane
                    id="grab-panel"
                    class={deviceData.canModify ? "collidable" : ""}
                    width={
                        width * 2 +
                        (data.state.scale < 0
                            ? (width * -data.state.scale) / 2
                            : 0)
                    }
                    height={
                        height * 2.5 +
                        (data.state.scale < 0
                            ? (height * -data.state.scale) / 2
                            : 0)
                    }
                    position={`0 ${height * 0.6} ${-depth * 0.7 + data.state.scale
                        }`}
                    ref={dragPanel}
                    visible={false}
                />
                <a-entity
                    position={`${data.state.position.x} ${data.state.position.y} ${data.state.scale}`}
                >
                    <TopologyWidgetAux
                        data={data.data}
                        width={width}
                        height={height}
                        depth={depth}
                        linkWidth={linkWidth}
                        linkDirectionalParticleWidth={linkDirectionalParticleWidth}
                        rootRef={root}
                        loading={loading}
                        setLoading={setLoading}
                        aframeClasses={deviceData.canModify ? "collidable" : ""}
                    />
                </a-entity>
            </a-entity>
        </a-entity>
    );
}

const TopologyWidgetAux = React.memo(
    ({
        width,
        depth,
        height,
        data,
        linkWidth,
        rootRef,
        loading,
        setLoading,
        aframeClasses,
        linkDirectionalParticleWidth
    }) => {
        return (
            <a-entity rotation="-90 0 0">
                <Panel
                    resize
                    width={width}
                    height={depth}
                    depth={height}
                    resizePrecision={1}
                    loading={loading}
                    setLoading={setLoading}
                    aframeClasses={aframeClasses}
                >
                    <a-entity rotation="90 0 0" ref={rootRef}>
                        <TopologyWidget
                            linkWidth={linkWidth}
                            linkOpacity={0.5}
                            data={data}
                            linkDirectionalParticles={3}
                            linkDirectionalParticleWidth={linkDirectionalParticleWidth}
                            dagMode="td"
                            levelDistance={50}
                        />
                    </a-entity>
                </Panel>
            </a-entity>
        );
    }
);

// requiring dataPoints
TopologyAdvanced.propTypes = {
    data: PropTypes.object.isRequired,
    deviceData: PropTypes.object.isRequired,
    setZoom: PropTypes.func.isRequired,
    setPosition: PropTypes.func.isRequired,
    setLockControls: PropTypes.func.isRequired
};

export default TopologyAdvanced;
